/**
 * @typedef {Object} CustomMetaProps
 * @property {string} [title]
 * @property {boolean} [canGoBack] - if true, the route will have a back button
 * @property {string} [backRouteName] - name of the route to go back to
 * @property {boolean} [anonymous] - if true, the route is accessible also for anonymous users
 * @property {boolean} [onlyAnonymous] - if true, the route is accessible only for anonymous users
 * @property {{ text: string, to: import('vue-router').RouteLocationRaw }[]} [breadcrumbs] - array of breadcrumbs
 * @property {(rights: ReturnType<typeof import('@/hooks/useRights').default>) => boolean} [access] - route access function that returns true if the user has access to the route
 */
/**
 * @typedef {import('vue-router').RouteRecordRaw & { meta?: CustomMetaProps, children?: CustomRoute[] }} CustomRoute
 */

/** @type {CustomRoute[]} */
const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: {
      title: 'pages.home.title',
    },
    children: [
      {
        path: '/login',
        name: 'login',
        meta: {
          title: 'pages.login.title',
          anonymous: true,
          category: 'useful',
        },
        component: () => import('@/views/Login/Login.vue'),
      },
      {
        path: '/dashboard',
        name: 'dashboard',
        meta: {
          title: 'pages.dashboard.title',
          anonymous: true,
          category: 'useful',
        },
        component: () => import('@/views/Dashboard.vue'),
      },
      {
        path: '/redirect/:target',
        name: 'redirect',
        meta: { anonymous: true },
        component: () => import('@/views/Redirect.vue'),
      },
      {
        path: '/user/profile',
        name: 'userProfile',
        component: () => import('@/views/UserProfile.vue'),
        meta: {
          title: 'pages.userProfile.title',
          anonymous: false,
        },
      },
      {
        path: '/sessionTimeout',
        name: 'sessionTimeout',
        component: () => import('@/views/SessionTimeout.vue'),
        meta: {
          title: 'pages.sessionTimeout.title',
          category: 'useful',
          anonymous: true,
        },
      },
      {
        path: '/error',
        name: 'error',
        alias: 'notFound',
        component: () => import('@/views/Error404.vue'),
        meta: {
          anonymous: true,
          category: 'useful',
          hideFromIndex: true,
        },
      },
      {
        path: '/forbidden',
        name: 'forbidden',
        component: () => import('@/views/Error403.vue'),
        meta: { anonymous: true },
      },
      {
        path: '/not-authorized',
        name: 'notAuthorized',
        component: () => import('@/views/Error401.vue'),
        meta: { anonymous: true },
      },
      {
        path: '/schedule',
        name: 'schedule',
        component: () => import('@/views/Schedule/Schedule.vue'),
        meta: {
          title: 'pages.schedule.title',
          description: 'pages.schedule.description',
          anonymous: true,
          breadcrumbs: [
            {
              text: 'pages.dashboard.title',
              to: { name: 'dashboard' },
            },
          ],
        },
      },
      {
        path: '/schedule/:id',
        name: 'scheduleItem',
        component: () => import('@/views/Schedule/ScheduleItem.vue'),
        meta: {
          title: 'pages.scheduleItem.title',
          description: 'pages.scheduleItem.description',
          anonymous: true,
          breadcrumbs: [
            {
              text: 'pages.schedule.title',
              to: { name: 'schedule' },
            },
          ],
        },
      },
      {
        path: '/schedule/:id/authorized',
        name: 'scheduleItemAuthorized',
        component: () => import('@/views/Schedule/ScheduleItem.vue'),
        meta: {
          title: 'pages.scheduleItem.title',
          description: 'pages.scheduleItem.description',
          anonymous: false,
          breadcrumbs: [
            {
              text: 'pages.schedule.title',
              to: { name: 'schedule' },
            },
          ],
        },
      },
      {
        path: '/users',
        name: 'userManagement',
        component: () => import('@/views/UserManagement.vue'),
        meta: {
          title: 'pages.userManagement.title',
          description: 'pages.userManagement.description',
          anonymous: false,
          breadcrumbs: [
            {
              text: 'pages.dashboard.title',
              to: { name: 'dashboard' },
            },
          ],
        },
      },
      {
        path: '/users/stats',
        name: 'userStats',
        component: () => import('@/views/UserStats.vue'),
        meta: {
          title: 'pages.userStats.title',
          description: 'pages.userStats.description',
          anonymous: false,
          breadcrumbs: [
            {
              text: 'pages.dashboard.title',
              to: { name: 'dashboard' },
            },
          ],
        },
      },
      {
        path: '/commissions',
        name: 'commissionManagement',
        component: () => import('@/views/CommissionManagement.vue'),
        meta: {
          title: 'pages.commissionManagement.title',
          description: 'pages.commissionManagement.description',
          anonymous: false,
          breadcrumbs: [
            {
              text: 'pages.dashboard.title',
              to: { name: 'dashboard' },
            },
          ],
        },
      },
      {
        path: '/admin/email-templates',
        name: 'emailTemplates',
        component: () => import('@/views/EmailTemplates.vue'),
        meta: {
          title: 'pages.emailTemplates.title',
          anonymous: false,
          breadcrumbs: [
            {
              text: 'pages.dashboard.title',
              to: { name: 'dashboard' },
            },
          ],
        },
      },
    ],
  },
];

export default routes;
